/**
 * Componentes IA embebidos para Análisis de Ventas.
 * - DiagnosticoIA: llama a /api/analisis-ventas/diagnostico bajo demanda (botón).
 * - ConsultorIA: mini-chat que reutiliza el pipeline del Chatbot embebido.
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Sparkles, Send, RefreshCw, Bot, ClipboardList } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { analisisVentasAPI } from '../../utils/api';
import { Card, COLOR_BRONCE_DARK } from './components';

const BRONCE_STYLE = { background: '#9d8272' };
const BRONCE_BTN_CLS = 'text-white hover:opacity-90';

export const DiagnosticoIA = ({ proyecto, etapa, tipologia, columna, dashboardLoading }) => {
  const [loading, setLoading] = useState(false);
  const [bullets, setBullets] = useState([]);

  // Reset cuando cambian filtros clave
  React.useEffect(() => {
    setBullets([]);
  }, [proyecto, etapa, tipologia, columna]);

  const generar = async () => {
    if (!proyecto) {
      toast.error('Selecciona un proyecto primero');
      return;
    }
    setLoading(true);
    try {
      const r = await analisisVentasAPI.diagnostico({
        proyecto,
        etapa,
        tipologia: tipologia === 'all' ? null : tipologia,
        columna: columna === 'all' ? null : columna,
      });
      setBullets(r.data.bullets || []);
      if (!r.data.bullets?.length) toast.info('El modelo no devolvió bullets');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error generando diagnóstico');
    } finally {
      setLoading(false);
    }
  };

  const hasBullets = bullets.length > 0;

  return (
    <Card
      icon={<ClipboardList size={14} />}
      title="Diagnóstico Estratégico Integral"
      action={
        <Button
          type="button"
          size="sm"
          onClick={generar}
          disabled={loading || !proyecto || dashboardLoading}
          data-testid="av-generar-diagnostico"
          className={`${BRONCE_BTN_CLS} h-7 px-2 text-[11px]`}
          style={BRONCE_STYLE}
        >
          {loading ? (
            <Loader2 size={12} className="mr-1 animate-spin" />
          ) : hasBullets ? (
            <RefreshCw size={12} className="mr-1" />
          ) : (
            <Sparkles size={12} className="mr-1" />
          )}
          {hasBullets ? 'Regenerar' : 'Generar'}
        </Button>
      }
      className="h-full flex flex-col"
    >
      <div className="flex-1 overflow-y-auto min-h-[140px]" data-testid="av-diagnostico">
        {!hasBullets && !loading && (
          <p className="text-sm skin-text-mute italic">
            Pulsa <strong>Generar</strong> para obtener un análisis ejecutivo basado en los KPIs vigentes.
          </p>
        )}
        {loading && (
          <div className="flex items-center gap-2 skin-text-soft text-sm">
            <Loader2 size={16} className="animate-spin" /> Analizando datos con IA…
          </div>
        )}
        <ul className="space-y-3 text-sm skin-text-soft mt-1">
          {bullets.map((b, i) => (
            <li
              key={i}
              className="pl-3 border-l-2 leading-relaxed"
              style={{ borderColor: 'var(--skin-accent)' }}
              data-testid={`av-diagnostico-bullet-${i}`}
            >
              <strong style={{ color: 'var(--skin-accent-dark)' }}>{b.titulo}:</strong>{' '}
              <span dangerouslySetInnerHTML={{
                __html: (b.texto || '').replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>'),
              }} />
            </li>
          ))}
        </ul>
      </div>
    </Card>
  );
};

export const ConsultorIA = ({ proyecto, etapa, tipologia, columna }) => {
  const [pregunta, setPregunta] = useState('');
  const [respuesta, setRespuesta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);

  const preguntar = async () => {
    if (!pregunta.trim()) return;
    if (!proyecto) {
      toast.error('Selecciona un proyecto primero');
      return;
    }
    setLoading(true);
    try {
      const r = await analisisVentasAPI.consultor({
        proyecto,
        etapa,
        tipologia: tipologia === 'all' ? null : tipologia,
        columna: columna === 'all' ? null : columna,
        pregunta,
        session_id: sessionId,
      });
      setRespuesta(r.data.payload);
      if (r.data.session_id) setSessionId(r.data.session_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error en el consultor IA');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card
      icon={<Bot size={14} />}
      title="Consultor de Estrategia Inmobiliaria (IA)"
      className="h-full flex flex-col"
    >
      <div className="flex-1 flex flex-col" data-testid="av-consultor">
        <div className="flex-1 rounded-lg p-3 min-h-[140px] mb-2 overflow-y-auto text-sm" style={{ background: 'var(--skin-input-bg)', border: '1px solid var(--skin-line)', color: 'var(--skin-ink-soft)' }}>
          {!respuesta && !loading && (
            <p className="italic">
              ¡Hola! Analizo en tiempo real los datos comerciales. Pregúntame sobre absorción, márgenes o precios de cierre.
            </p>
          )}
          {loading && (
            <div className="flex items-center gap-2">
              <Loader2 size={16} className="animate-spin" /> Pensando…
            </div>
          )}
          {respuesta && !loading && (
            <ConsultorResponsePayload payload={respuesta} />
          )}
        </div>
        <div className="flex items-end gap-2">
          <Textarea
            value={pregunta}
            onChange={(e) => setPregunta(e.target.value)}
            placeholder="Escriba aquí…"
            rows={2}
            data-testid="av-consultor-textarea"
            className="skin-input"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) preguntar();
            }}
          />
          <Button
            type="button"
            onClick={preguntar}
            disabled={loading || !pregunta.trim() || !proyecto}
            data-testid="av-consultor-preguntar"
            className={BRONCE_BTN_CLS}
            style={BRONCE_STYLE}
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            <span className="ml-1 hidden sm:inline">Preguntar</span>
          </Button>
        </div>
      </div>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Formateo declarativo por `format` — helper compartido para todos los renderers.
// Reconoce: 'currency', 'percent', 'number', 'text', 'date' (más fallback).
// ---------------------------------------------------------------------------
const nfNumberPE = new Intl.NumberFormat('es-PE');
const nfCurrencyPE = new Intl.NumberFormat('es-PE', {
  style: 'currency', currency: 'PEN', maximumFractionDigits: 0,
});

const formatByType = (value, format) => {
  if (value === null || value === undefined || value === '') return '—';
  const fmt = String(format || '').toLowerCase();
  if (fmt === 'currency') {
    const n = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(n) ? nfCurrencyPE.format(Math.round(n)) : String(value);
  }
  if (fmt === 'percent') {
    const n = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(n)) return String(value);
    // Heurística: si viene 0-1 se asume ratio (multiplicar 100); si >1 se asume ya en %.
    const pct = Math.abs(n) <= 1 ? n * 100 : n;
    return `${pct.toFixed(2).replace(/\.?0+$/, '')}%`;
  }
  if (fmt === 'number') {
    const n = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(n) ? nfNumberPE.format(Math.round(n)) : String(value);
  }
  if (fmt === 'date') {
    try {
      const d = new Date(value);
      if (!isNaN(d.getTime())) {
        return d.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });
      }
    } catch { /* ignore */ }
    return String(value);
  }
  // Fallback: si es número, formatear con separadores; si no, string tal cual.
  if (typeof value === 'number' && Number.isFinite(value)) return nfNumberPE.format(value);
  return String(value);
};

// Detecta si el `columns` viene como array de objetos {key,label,format}
// (payload moderno) vs array de strings (legacy). Devuelve estructura normalizada.
const normalizeColumns = (cols, rows) => {
  if (!Array.isArray(cols) || cols.length === 0) {
    const first = rows?.[0] || {};
    return Object.keys(first).map((k) => ({ key: k, label: k, format: null }));
  }
  if (typeof cols[0] === 'string') {
    return cols.map((k) => ({ key: k, label: k, format: null }));
  }
  return cols.map((c) => ({
    key: c.key,
    label: c.label || c.key,
    format: c.format || null,
  }));
};

// ---------------------------------------------------------------------------
// Render del payload del Consultor / Chatbot.
// Cubre: cannot_answer, kpi/metric, metric_with_context, metric_by_tipo,
//        generic_table, ranking_table, table (legacy) y fallback amistoso.
// ---------------------------------------------------------------------------
const ConsultorResponsePayload = ({ payload }) => {
  if (!payload) return null;
  const type = payload.type;

  // --- cannot_answer / empty --------------------------------------------------
  if (type === 'cannot_answer' || type === 'empty') {
    return (
      <div
        className="text-sm rounded p-3 border"
        style={{
          color: 'var(--skin-accent-dark)',
          background: 'var(--skin-line-soft)',
          borderColor: 'var(--skin-line)',
        }}
        data-testid="consultor-cannot-answer"
      >
        {payload.message ||
          'No pude responder esa pregunta con los datos disponibles.'}
      </div>
    );
  }

  // --- metric_with_context ----------------------------------------------------
  // Titular = primer item texto del context (respuesta directa).
  // Subordinados = resto del context como tabla clave-valor + KPI value.
  if (type === 'metric_with_context') {
    const contextArr = Array.isArray(payload.context) ? payload.context : [];
    const heroCtx = contextArr.find(
      (c) => c && typeof c === 'object' && (c.format === 'text' || typeof c.value === 'string')
    );
    const restCtx = contextArr.filter((c) => c !== heroCtx);
    const kpi = payload.kpi || {};
    const kpiLabel = kpi.label || payload.title || 'Resultado';
    const kpiVal = formatByType(
      kpi.value_formatted ?? kpi.value ?? payload.value_formatted ?? payload.value,
      kpi.value_format || 'number',
    );

    return (
      <div className="skin-text-ink" data-testid="consultor-metric-ctx">
        {payload.title && (
          <p className="skin-field-label mb-2">{payload.title}</p>
        )}
        {heroCtx ? (
          <div className="mb-3">
            <p className="text-[10px] uppercase tracking-wider skin-text-mute">
              {heroCtx.label}
            </p>
            <p
              className="text-3xl font-bold leading-none mt-1"
              style={{
                fontFamily: 'var(--skin-font-display)',
                color: 'var(--skin-accent-dark)',
              }}
            >
              {formatByType(heroCtx.value, heroCtx.format)}
            </p>
          </div>
        ) : (
          <p
            className="text-2xl font-bold mb-3"
            style={{
              fontFamily: 'var(--skin-font-display)',
              color: 'var(--skin-accent-dark)',
            }}
          >
            {kpiVal}
          </p>
        )}
        <div
          className="rounded overflow-hidden"
          style={{ border: '1px solid var(--skin-line)' }}
        >
          {heroCtx && (
            <div
              className="flex justify-between px-3 py-1.5 text-xs"
              style={{ background: 'var(--skin-line-soft)' }}
            >
              <span className="skin-text-mute">{kpiLabel}</span>
              <span className="font-semibold skin-text-ink">{kpiVal}</span>
            </div>
          )}
          {restCtx.map((c, i) => (
            <div
              key={i}
              className="flex justify-between px-3 py-1.5 text-xs"
              style={{ borderTop: '1px solid var(--skin-line-soft)' }}
            >
              <span className="skin-text-mute">{c.label}</span>
              <span className="font-semibold skin-text-ink">
                {formatByType(c.value, c.format)}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // --- kpi / metric (simple, sin context) ------------------------------------
  if (type === 'kpi' || type === 'metric') {
    const inner = payload.kpi || payload;
    const rawVal = inner.value_formatted ?? inner.value ?? payload.value_formatted ?? payload.value;
    const val = inner.value_formatted != null || payload.value_formatted != null
      ? rawVal
      : formatByType(rawVal, inner.value_format || payload.value_format || 'number');
    const label = inner.label || payload.title;
    return (
      <div className="skin-text-ink" data-testid="consultor-kpi">
        <p className="skin-field-label">{label}</p>
        <p
          className="text-3xl font-bold mt-1"
          style={{
            fontFamily: 'var(--skin-font-display)',
            color: 'var(--skin-accent-dark)',
          }}
        >
          {val ?? '—'}
        </p>
        {payload.subtitle && (
          <p className="text-xs skin-text-mute mt-1">{payload.subtitle}</p>
        )}
      </div>
    );
  }

  // --- metric_by_tipo ---------------------------------------------------------
  if (type === 'metric_by_tipo' && Array.isArray(payload.items)) {
    return (
      <div data-testid="consultor-metric-by-tipo">
        <p className="skin-field-label mb-2">{payload.title}</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {payload.items.map((it, i) => (
            <div
              key={i}
              className="rounded p-2"
              style={{
                background: 'var(--skin-panel)',
                border: '1px solid var(--skin-line)',
              }}
            >
              <p className="text-[10px] skin-text-mute uppercase">{it.tipo}</p>
              <p className="text-sm font-semibold skin-text-ink">
                {payload.currency ? `${payload.currency} ` : ''}
                {typeof it.valor === 'number' ? nfNumberPE.format(Math.round(it.valor)) : '—'}
                {payload.unit || ''}
              </p>
              <p className="text-[10px] skin-text-mute">
                {it.n_validos}/{it.n} unidades
              </p>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // --- generic_table / ranking_table / table (legacy) ------------------------
  if ((type === 'generic_table' || type === 'ranking_table' || type === 'table') &&
      Array.isArray(payload.rows)) {
    const cols = normalizeColumns(payload.columns, payload.rows);
    const rows = payload.rows;
    const isRanking = type === 'ranking_table';
    const shown = rows.slice(0, 20);
    const rest = rows.length - shown.length;
    return (
      <div className="overflow-x-auto" data-testid={`consultor-${type}`}>
        <div className="flex items-center justify-between mb-1">
          <p className="skin-field-label">{payload.title}</p>
          {isRanking && (
            <span
              className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded"
              style={{
                background: 'var(--skin-accent)',
                color: '#fff',
                fontWeight: 700,
              }}
            >
              TOP {payload.top ?? rows.length}
            </span>
          )}
        </div>
        <table
          className="w-full text-xs rounded overflow-hidden"
          style={{ border: '1px solid var(--skin-line)' }}
        >
          <thead>
            <tr style={{ background: 'var(--skin-line-soft)' }}>
              {isRanking && (
                <th className="px-2 py-1.5 text-left skin-text-mute font-semibold">#</th>
              )}
              {cols.map((c) => (
                <th
                  key={c.key}
                  className="px-2 py-1.5 text-left skin-text-mute font-semibold"
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((r, i) => {
              const highlight = isRanking && i === 0;
              return (
                <tr
                  key={i}
                  style={{
                    borderTop: '1px solid var(--skin-line-soft)',
                    background: highlight ? 'var(--skin-line-soft)' : 'transparent',
                  }}
                >
                  {isRanking && (
                    <td
                      className="px-2 py-1.5 font-semibold"
                      style={{
                        color: highlight ? 'var(--skin-accent-dark)' : 'var(--skin-ink-mute)',
                      }}
                    >
                      {highlight ? '🏆' : i + 1}
                    </td>
                  )}
                  {cols.map((c) => (
                    <td key={c.key} className="px-2 py-1.5 skin-text-ink">
                      {formatByType(r[c.key], c.format)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
        {rest > 0 && (
          <p className="text-[11px] skin-text-mute mt-1 italic">
            …y {rest} registro{rest === 1 ? '' : 's'} más
          </p>
        )}
      </div>
    );
  }

  // --- Fallback amistoso (nunca dumpar JSON crudo al usuario final) ----------
  // El detalle se loggea a consola para debug del equipo, pero al usuario le
  // mostramos un mensaje claro.
  if (typeof console !== 'undefined' && console.warn) {
    console.warn('[Consultor] Tipo de payload no soportado:', payload);
  }
  return (
    <div
      className="text-sm rounded p-3 border"
      style={{
        color: 'var(--skin-accent-dark)',
        background: 'var(--skin-line-soft)',
        borderColor: 'var(--skin-line)',
      }}
      data-testid="consultor-fallback"
    >
      {payload.title && (
        <p className="font-semibold mb-1">{payload.title}</p>
      )}
      <p className="text-xs skin-text-mute">
        Tengo una respuesta pero aún no sé cómo mostrarla en este formato. Intenta
        replantear la pregunta o contacta a soporte si el problema persiste.
      </p>
    </div>
  );
};
